import { IoMdDownload } from '@react-icons/all-files/io/IoMdDownload'
import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  ImageRun,
  LevelFormat,
  Packer,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType
} from 'docx'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { useCallback, useEffect, useRef, useState } from 'react'
import rehypeRaw from 'rehype-raw'
import rehypeStringify from 'rehype-stringify'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import remarkRehype from 'remark-rehype'
import { unified } from 'unified'
import { PopoverNavDownIcon } from '~/components/icons'
import { prepareDocMarkdown } from '~/components/documents/DocMarkdown'
import { BetterToast } from '~/utils/toast'
import { DownloadProgressButton } from './DownloadProgressButton'
import { SelectorDropdownContent, SelectorDropdownItem } from './SelectorDropdown'

interface DownloadDocumentDropdownProps {
  content: string
  filename?: string
  label?: string
  variant?: 'primary' | 'toolbar'
}

type MdNode = {
  type: string
  children?: MdNode[]
  value?: string
  alt?: string
  depth?: number
  ordered?: boolean
  url?: string
  lang?: string
  align?: (string | null)[]
  identifier?: string
  label?: string
  referenceType?: string
}

// The export pipeline (docx/pdf/md) doesn't go through the SafeMarkdown renderer,
// so it canonicalizes here. Single source of truth is `prepareDocMarkdown` in
// markdownRules — this thin wrapper keeps the existing export-path call site and
// the importers (DownloadAllDocsDropdown, EditableCanvasSplitScreen) stable.
function preprocessContent(content: string): string {
  return prepareDocMarkdown(content)
}

const HEADING_MAP: Record<number, (typeof HeadingLevel)[keyof typeof HeadingLevel]> = {
  1: HeadingLevel.HEADING_1,
  2: HeadingLevel.HEADING_2,
  3: HeadingLevel.HEADING_3,
  4: HeadingLevel.HEADING_4,
  5: HeadingLevel.HEADING_5,
  6: HeadingLevel.HEADING_6
}

const DEFAULT_IMAGE_DIMENSIONS = { width: 600, height: 400 }

interface InlineStyle {
  bold?: boolean
  italics?: boolean
}

function base64ToBinary(base64: string): Uint8Array {
  const base64Data = base64.includes('base64,') ? base64.split('base64,')[1] : base64
  const binaryString = atob(base64Data)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i)
  return bytes
}

function normalizeImageDimensions(dims: { width: number; height: number }): {
  width: number
  height: number
} {
  if (
    Number.isFinite(dims.width) &&
    Number.isFinite(dims.height) &&
    dims.width > 0 &&
    dims.height > 0
  ) {
    return {
      width: Math.round(dims.width),
      height: Math.round(dims.height)
    }
  }
  return DEFAULT_IMAGE_DIMENSIONS
}

function getDimensionsFromBytes(bytes: Uint8Array): { width: number; height: number } {
  try {
    if (bytes[0] === 0x89 && bytes[1] === 0x50) {
      return normalizeImageDimensions({
        width: (bytes[16] << 24) | (bytes[17] << 16) | (bytes[18] << 8) | bytes[19],
        height: (bytes[20] << 24) | (bytes[21] << 16) | (bytes[22] << 8) | bytes[23]
      })
    }
    if (bytes[0] === 0xff && bytes[1] === 0xd8) {
      let pos = 2
      while (pos < bytes.length) {
        if (bytes[pos] !== 0xff) {
          pos++
          continue
        }
        if (bytes[pos + 1] === 0xc0 || bytes[pos + 1] === 0xc2) {
          return normalizeImageDimensions({
            width: (bytes[pos + 7] << 8) | bytes[pos + 8],
            height: (bytes[pos + 5] << 8) | bytes[pos + 6]
          })
        }
        pos += 2 + ((bytes[pos + 2] << 8) | bytes[pos + 3])
      }
    }
  } catch (err) {
    console.error('[DOCX] Error parsing image dimensions:', err)
  }
  return DEFAULT_IMAGE_DIMENSIONS
}

async function createImageParagraph(imageUrl: string, altText?: string): Promise<Paragraph> {
  try {
    let imageData: string | Uint8Array
    let dims: { width: number; height: number }

    if (imageUrl.startsWith('data:')) {
      // Pass data URI string directly to ImageRun — the library's own
      // convertDataURIToBinary handles the decoding internally.
      // We still decode separately for dimension parsing only.
      imageData = imageUrl
      const decoded = base64ToBinary(imageUrl)
      dims = getDimensionsFromBytes(decoded)
      console.log('[DOCX] Image from data URI:', decoded.length, 'bytes, dims:', dims)
    } else {
      const response = await fetch(imageUrl, { mode: 'cors' })
      if (!response.ok) {
        console.warn('[DOCX] Image fetch failed:', response.status, imageUrl.substring(0, 80))
        throw new Error(`HTTP ${response.status}`)
      }
      const buffer = await response.arrayBuffer()
      imageData = new Uint8Array(buffer)
      dims = getDimensionsFromBytes(imageData)
      console.log('[DOCX] Image from URL:', imageData.length, 'bytes, dims:', dims)
    }

    dims = normalizeImageDimensions(dims)
    const aspectRatio = dims.width / dims.height
    const maxWidth = 600
    const newHeight = Math.round(maxWidth / aspectRatio)

    return new Paragraph({
      children: [
        new ImageRun({
          data: imageData,
          transformation: { width: maxWidth, height: newHeight }
        })
      ],
      spacing: { before: 240, after: 240 },
      alignment: AlignmentType.CENTER
    })
  } catch (err) {
    console.error('[DOCX] createImageParagraph failed for', imageUrl.substring(0, 80), ':', err)
    return new Paragraph({
      children: [new TextRun({ text: altText ? `[Image: ${altText}]` : '[Image]', italics: true })],
      spacing: { after: 200 }
    })
  }
}

function extractInlineRuns(node: MdNode, style: InlineStyle = {}): TextRun[] {
  if (node.type === 'text') {
    return [new TextRun({ text: node.value ?? '', ...style })]
  }
  if (node.type === 'inlineCode') {
    return [new TextRun({ text: node.value ?? '', font: 'Courier New', size: 20, ...style })]
  }
  if (node.type === 'strong') {
    return (node.children ?? []).flatMap(c => extractInlineRuns(c, { ...style, bold: true }))
  }
  if (node.type === 'emphasis') {
    return (node.children ?? []).flatMap(c => extractInlineRuns(c, { ...style, italics: true }))
  }
  if (node.type === 'link') {
    const linkText = (node.children ?? []).flatMap(c => extractInlineRuns(c, style))
    if (linkText.length === 0) linkText.push(new TextRun({ text: node.url ?? '', ...style }))
    return linkText
  }
  if (node.type === 'delete') {
    return (node.children ?? []).flatMap(c =>
      extractInlineRuns(c, style).map(
        r => new TextRun({ text: (r as any).options?.text ?? '', ...style, strike: true })
      )
    )
  }
  if (node.type === 'image') {
    return [new TextRun({ text: node.alt ?? '[image]', italics: true })]
  }
  if (node.type === 'imageReference') {
    return [new TextRun({ text: node.alt ?? node.label ?? '[image]', italics: true })]
  }
  if (node.children) return node.children.flatMap(c => extractInlineRuns(c, style))
  if (node.value) return [new TextRun({ text: node.value, ...style })]
  return []
}

let listInstanceCounter = 0
const numberingConfigs: { reference: string; levels: any[] }[] = []

function createNumberingConfig(): string {
  const reference = `md-list-${++listInstanceCounter}`
  numberingConfigs.push({
    reference,
    levels: Array.from({ length: 9 }, (_, i) => ({
      level: i,
      text: i === 0 ? '%1. ' : `%${i + 1}. `,
      format: i === 0 ? LevelFormat.DECIMAL : LevelFormat.LOWER_LETTER,
      style: {
        paragraph: {
          indent: { left: (i + 1) * 360, hanging: 240 },
          spacing: { before: 120, after: 120 }
        }
      },
      alignment: AlignmentType.LEFT,
      start: 1
    }))
  })
  return reference
}

async function convertListItems(
  node: MdNode,
  ordered: boolean,
  level: number,
  numberingRef?: string
): Promise<Paragraph[]> {
  const results: Paragraph[] = []
  const ref = ordered ? (numberingRef ?? createNumberingConfig()) : undefined

  for (const [, item] of (node.children ?? []).entries()) {
    for (const child of item.children ?? []) {
      if (child.type === 'paragraph') {
        results.push(
          new Paragraph({
            children: child.children!.flatMap(c => extractInlineRuns(c)),
            ...(ordered && ref
              ? { numbering: { reference: ref, level: Math.min(level, 8) } }
              : { bullet: { level: Math.min(level, 8) } }),
            spacing: { before: 120, after: 120 }
          })
        )
      } else if (child.type === 'list') {
        results.push(...(await convertListItems(child, !!child.ordered, level + 1, ref)))
      }
    }
  }
  return results
}

function getTextLength(node: MdNode): number {
  if (node.value) return node.value.length
  return (node.children ?? []).reduce((sum, c) => sum + getTextLength(c), 0)
}

function convertTable(node: MdNode): Table {
  const allRows = node.children ?? []
  // Size to the widest row, not just the header: a ragged table where a body
  // row has more cells than the header would otherwise index past this array,
  // turning maxContentLengths[colIdx] into undefined -> Math.max -> NaN, which
  // poisons every columnWidth and makes the docx `new Table` constructor throw.
  const numCols = Math.max(1, ...allRows.map(r => r.children?.length ?? 0))
  const maxContentLengths = new Array<number>(numCols).fill(1)
  for (const row of allRows) {
    for (const [colIdx, cell] of (row.children ?? []).entries()) {
      maxContentLengths[colIdx] = Math.max(maxContentLengths[colIdx], getTextLength(cell))
    }
  }
  const totalContent = maxContentLengths.reduce((s, l) => s + l, 0)
  const columnWidths = maxContentLengths.map(l => Math.floor((l / totalContent) * 9000))

  const rows = allRows.map((row, rowIdx) => {
    const cells = (row.children ?? []).map(
      (cell, colIdx) =>
        new TableCell({
          children: [
            new Paragraph({
              children: (cell.children ?? []).flatMap(c =>
                extractInlineRuns(c, rowIdx === 0 ? { bold: true } : {})
              ),
              alignment:
                node.align?.[colIdx] === 'center'
                  ? AlignmentType.CENTER
                  : node.align?.[colIdx] === 'right'
                    ? AlignmentType.RIGHT
                    : AlignmentType.LEFT
            })
          ],
          shading: rowIdx === 0 ? { fill: 'E8E8E8' } : undefined
        })
    )
    return new TableRow({ children: cells })
  })

  return new Table({ rows, width: { size: 100, type: WidthType.AUTO }, columnWidths })
}

type DefinitionMap = Map<string, string>

async function convertNode(node: MdNode, defs: DefinitionMap): Promise<(Paragraph | Table)[]> {
  switch (node.type) {
    case 'heading':
      return [
        new Paragraph({
          children: (node.children ?? []).flatMap(c => extractInlineRuns(c)),
          heading: HEADING_MAP[node.depth ?? 1],
          spacing: { before: 240, after: 120 }
        })
      ]

    case 'paragraph': {
      const results: (Paragraph | Table)[] = []
      const imageTypes = new Set(['image', 'imageReference'])
      const imageChildren = (node.children ?? []).filter(c => imageTypes.has(c.type))
      const nonImageChildren = (node.children ?? []).filter(c => !imageTypes.has(c.type))

      if (nonImageChildren.length > 0) {
        results.push(
          new Paragraph({
            children: nonImageChildren.flatMap(c => extractInlineRuns(c)),
            spacing: { after: 200 }
          })
        )
      }
      for (const img of imageChildren) {
        if (img.type === 'imageReference') {
          const url = defs.get(img.identifier ?? '') ?? ''
          console.log(
            '[DOCX] Resolved imageReference',
            img.identifier,
            '-> url length:',
            url.length
          )
          if (url) results.push(await createImageParagraph(url, img.alt ?? img.label))
        } else {
          console.log(
            '[DOCX] Found image in paragraph, url length:',
            (img.url ?? '').length,
            'alt:',
            img.alt
          )
          results.push(await createImageParagraph(img.url ?? '', img.alt))
        }
      }
      return results.length > 0 ? results : [new Paragraph({ spacing: { after: 200 } })]
    }

    case 'image':
      console.log(
        '[DOCX] Found standalone image, url length:',
        (node.url ?? '').length,
        'alt:',
        node.alt
      )
      return [await createImageParagraph(node.url ?? '', node.alt)]

    case 'imageReference': {
      const url = defs.get(node.identifier ?? '') ?? ''
      console.log(
        '[DOCX] Resolved standalone imageReference',
        node.identifier,
        '-> url length:',
        url.length
      )
      if (url) return [await createImageParagraph(url, node.alt ?? node.label)]
      return []
    }

    case 'definition':
      return []

    case 'blockquote': {
      const items = await Promise.all((node.children ?? []).map(c => convertNode(c, defs)))
      return items.flat().map(item => {
        if (item instanceof Paragraph) {
          return new Paragraph({
            ...(item as any).options,
            indent: { left: 720, right: 720 },
            border: { left: { color: '999999', space: 10, size: 20, style: BorderStyle.SINGLE } },
            shading: { type: ShadingType.CLEAR, fill: 'FFFFE0' },
            spacing: { before: 240, after: 240 }
          })
        }
        return item
      })
    }

    case 'code': {
      const lines = (node.value ?? '').split('\n')
      return lines.map(
        (line, index) =>
          new Paragraph({
            children: [
              new TextRun({
                text: line.replace(/\t/g, '    ') || ' ',
                font: 'Courier New',
                size: 20
              })
            ],
            spacing: {
              before: index === 0 ? 120 : 0,
              after: index === lines.length - 1 ? 120 : 0,
              line: 240
            },
            shading: { type: ShadingType.CLEAR, fill: 'F8F8F8' },
            indent: { left: 360 },
            keepNext: true,
            keepLines: true
          })
      )
    }

    case 'list':
      return convertListItems(node, !!node.ordered, 0)

    case 'table':
      return [convertTable(node)]

    case 'thematicBreak':
      return [
        new Paragraph({
          border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' } },
          spacing: { before: 200, after: 200 }
        })
      ]

    case 'html': {
      if (!node.value?.trim()) return []
      const imgMatch = node.value.match(
        /<img[^>]+src=["']([^"']+)["'][^>]*(?:alt=["']([^"']*?)["'])?/i
      )
      if (imgMatch) {
        console.log('[DOCX] Found image in HTML node, src length:', imgMatch[1].length)
        return [await createImageParagraph(imgMatch[1], imgMatch[2])]
      }
      return [
        new Paragraph({
          children: [new TextRun({ text: node.value.replace(/<[^>]*>/g, '') })],
          spacing: { after: 200 }
        })
      ]
    }

    default:
      return []
  }
}

function markdownToHtml(markdown: string): string {
  return String(
    unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkRehype, { allowDangerousHtml: true })
      .use(rehypeRaw)
      .use(rehypeStringify)
      .processSync(markdown)
  )
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

const PDF_RENDER_STYLE = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { width: 760px; padding: 40px; background: white; color: #1a1a1a; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 13px; line-height: 1.6; }
  h1 { font-size: 22px; font-weight: 700; margin: 24px 0 8px; border-bottom: 1px solid #e0e0e0; padding-bottom: 6px; }
  h2 { font-size: 18px; font-weight: 600; margin: 20px 0 6px; }
  h3 { font-size: 15px; font-weight: 600; margin: 16px 0 4px; }
  h4, h5, h6 { font-size: 13px; font-weight: 600; margin: 12px 0 4px; }
  p { margin: 0 0 10px; }
  ul, ol { margin: 4px 0 10px; padding-left: 24px; }
  li { margin: 2px 0; }
  code { font-family: Menlo, Consolas, monospace; font-size: 11px; color: #c7254e; word-break: break-word; }
  pre { background: #f8f8f8; border: 1px solid #e0e0e0; border-radius: 4px; padding: 12px; margin: 10px 0; overflow: hidden; word-wrap: break-word; white-space: pre-wrap; }
  pre code { color: #1a1a1a; font-size: 10px; line-height: 1.5; word-break: normal; }
  table { border-collapse: collapse; width: 100%; margin: 10px 0; font-size: 11px; }
  th, td { border: 1px solid #d0d0d0; padding: 6px 10px; text-align: left; word-break: break-word; }
  th { background: #f0f0f0; font-weight: 600; }
  blockquote { border-left: 3px solid #ccc; margin: 10px 0; padding: 4px 16px; color: #555; background: #fafafa; }
  img { max-width: 100%; height: auto; margin: 8px 0; display: block; }
  hr { border: none; border-top: 1px solid #e0e0e0; margin: 16px 0; }
  a { color: #0366d6; text-decoration: none; }
`

export const RENDERED_HTML_EXPORT_STYLE = `
  html,
  body {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .break-before-page,
  .break-after-page,
  [style*="break-before:page" i],
  [style*="break-before: page" i],
  [style*="break-after:page" i],
  [style*="break-after: page" i],
  [style*="page-break-before:always" i],
  [style*="page-break-before: always" i],
  [style*="page-break-after:always" i],
  [style*="page-break-after: always" i] {
    break-before: auto !important;
    break-after: auto !important;
    page-break-before: auto !important;
    page-break-after: auto !important;
  }

  .h-screen,
  .min-h-screen,
  .h-dvh,
  .min-h-dvh,
  [class*="h-[100"],
  [class*="min-h-[100"],
  [style*="height:100vh" i],
  [style*="height: 100vh" i],
  [style*="height:100dvh" i],
  [style*="height: 100dvh" i],
  [style*="min-height:100vh" i],
  [style*="min-height: 100vh" i],
  [style*="min-height:100dvh" i],
  [style*="min-height: 100dvh" i] {
    height: auto !important;
    min-height: auto !important;
  }
`

const RENDERED_HTML_EXPORT_STYLE_TAG = `<style id="curie-rendered-html-export-style">${RENDERED_HTML_EXPORT_STYLE}</style>`

const PDF_IFRAME_HTML = (head: string, body: string) =>
  `<!DOCTYPE html><html><head>${head}</head><body>${body}</body></html>`

function makeHiddenIframe(width = 760): HTMLIFrameElement {
  const iframe = document.createElement('iframe')
  // Allow parent access for html2canvas measurement while blocking script
  // execution in the rendered document.
  iframe.setAttribute('sandbox', 'allow-same-origin')
  iframe.style.cssText = `position:fixed;left:-9999px;top:0;width:${width}px;height:0;border:none;visibility:hidden;`
  document.body.appendChild(iframe)
  return iframe
}

async function waitForImages(root: globalThis.Document | HTMLElement): Promise<void> {
  const imgs = root.querySelectorAll('img')
  if (imgs.length === 0) return
  await Promise.all(
    Array.from(imgs).map(
      img =>
        new Promise<void>(resolve => {
          if (img.complete && img.naturalHeight > 0) return resolve()
          img.onload = () => resolve()
          img.onerror = () => resolve()
          setTimeout(resolve, 5000)
        })
    )
  )
}

const TALL_EMPTY_CONTAINER_MIN_HEIGHT = 480
const TALL_EMPTY_CONTAINER_MIN_GAP = 160
const MEDIA_CONTAINER_TAGS = new Set(['IMG', 'CANVAS', 'SVG', 'VIDEO', 'IFRAME', 'OBJECT', 'EMBED'])

function shouldSkipExportNormalization(element: HTMLElement): boolean {
  if (MEDIA_CONTAINER_TAGS.has(element.tagName)) return true
  const style = element.ownerDocument.defaultView?.getComputedStyle(element)
  if (!style) return true
  return style.display === 'none' || style.visibility === 'hidden'
}

function getChildContentBottom(element: HTMLElement, elementTop: number): number {
  let bottom = 0
  const view = element.ownerDocument.defaultView
  if (!view) return bottom

  for (const child of Array.from(element.children)) {
    if (!(child instanceof view.HTMLElement) || shouldSkipExportNormalization(child)) continue
    const rect = child.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) continue
    bottom = Math.max(bottom, rect.bottom - elementTop)
  }
  return bottom
}

function normalizeRenderedHtmlDocumentForExport(doc: globalThis.Document): void {
  const elements = Array.from(doc.body.querySelectorAll<HTMLElement>('*'))

  // Work inside-out and run twice so collapsing a child can expose a tall
  // page wrapper that only existed to reserve print/viewport space.
  for (let pass = 0; pass < 2; pass++) {
    for (let i = elements.length - 1; i >= 0; i--) {
      const element = elements[i]
      if (shouldSkipExportNormalization(element)) continue

      const rect = element.getBoundingClientRect()
      const elementHeight = Math.max(rect.height, element.offsetHeight)
      if (elementHeight < TALL_EMPTY_CONTAINER_MIN_HEIGHT) continue

      const contentBottom = getChildContentBottom(element, rect.top)
      if (contentBottom <= 0) continue
      if (elementHeight - contentBottom < TALL_EMPTY_CONTAINER_MIN_GAP) continue

      element.style.setProperty('height', 'auto', 'important')
      element.style.setProperty('min-height', '0', 'important')
      element.style.setProperty('max-height', 'none', 'important')
      element.style.setProperty('overflow', 'visible', 'important')
      element.style.setProperty('break-before', 'auto', 'important')
      element.style.setProperty('break-after', 'auto', 'important')
      element.style.setProperty('page-break-before', 'auto', 'important')
      element.style.setProperty('page-break-after', 'auto', 'important')
      element.setAttribute('data-curie-export-normalized', 'true')
    }
  }
}

const PDF_PAGE_MARGIN_MM = 10
const PDF_IMAGE_WIDTH_MM = 190
const PDF_PAGE_CONTENT_BOTTOM_MM = 287
const PDF_MIN_REMAINING_HEIGHT_MM = 12

function paginateCanvasIntoPdf(
  canvas: HTMLCanvasElement,
  pdf: jsPDF,
  cursorY = PDF_PAGE_MARGIN_MM,
  imageQuality = 0.92
): number {
  const imgHeight = (canvas.height * PDF_IMAGE_WIDTH_MM) / canvas.width
  let sourceY = 0
  let nextY = cursorY

  while (sourceY < canvas.height) {
    let availableHeight = PDF_PAGE_CONTENT_BOTTOM_MM - nextY
    if (availableHeight < PDF_MIN_REMAINING_HEIGHT_MM) {
      pdf.addPage()
      nextY = PDF_PAGE_MARGIN_MM
      availableHeight = PDF_PAGE_CONTENT_BOTTOM_MM - nextY
    }

    const remainingSourceHeight = canvas.height - sourceY
    const sourceHeight = Math.max(
      1,
      Math.min(Math.floor((availableHeight / imgHeight) * canvas.height), remainingSourceHeight)
    )
    const destHeight = Math.min((sourceHeight * PDF_IMAGE_WIDTH_MM) / canvas.width, availableHeight)
    const imageCanvas =
      sourceY === 0 && sourceHeight === remainingSourceHeight
        ? canvas
        : document.createElement('canvas')

    if (imageCanvas !== canvas) {
      imageCanvas.width = canvas.width
      imageCanvas.height = sourceHeight
      const ctx = imageCanvas.getContext('2d')
      if (!ctx) throw new Error('Failed to acquire 2D context for PDF page slicing')
      ctx.drawImage(
        canvas,
        0,
        sourceY,
        canvas.width,
        sourceHeight,
        0,
        0,
        canvas.width,
        sourceHeight
      )
    }

    pdf.addImage(
      imageCanvas.toDataURL('image/jpeg', imageQuality),
      'JPEG',
      PDF_PAGE_MARGIN_MM,
      nextY,
      PDF_IMAGE_WIDTH_MM,
      destHeight
    )

    sourceY += sourceHeight
    nextY += destHeight
    if (sourceY < canvas.height) {
      pdf.addPage()
      nextY = PDF_PAGE_MARGIN_MM
    }
  }

  return nextY
}

// Browser canvas limits: Safari ≤ 16384 px per dimension, Chrome ≤ 32767 px,
// total area ≤ ~268M px on Chrome. When html2canvas exceeds the limit, it
// silently returns an oversized backing buffer the browser cannot allocate
// and fills it with black — every PDF page becomes a black rectangle.
//
// Strategy: render the source HTML in chunks of top-level children, each
// chunk small enough that `chunkSourceHeight × scale ≤ MAX_CANVAS_HEIGHT`.
// Each chunk produces its own canvas and gets paginated into the PDF.
// This scales linearly with document size and never produces an oversized canvas.
const MAX_CANVAS_HEIGHT = 14000
const DEFAULT_PDF_RENDER_SCALE = 2
const RENDERED_HTML_PDF_RENDER_SCALE = 1.5
const DEFAULT_PDF_IMAGE_QUALITY = 0.92
const RENDERED_HTML_PDF_IMAGE_QUALITY = 0.88
const MAX_CHUNK_SOURCE_HEIGHT = Math.floor(MAX_CANVAS_HEIGHT / DEFAULT_PDF_RENDER_SCALE)

interface ChildInfo {
  html: string
  height: number
}

interface PdfHtmlParts {
  head: string
  body: string
  renderWidth?: number
  renderScale?: number
  imageQuality?: number
  compactLargeBlankRows?: boolean
}

interface RenderedHtmlPdfOptions {
  renderWidth?: number
}

function parseFullHtmlDocument(html: string): PdfHtmlParts {
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  return {
    head: parsed.head.innerHTML,
    body: parsed.body.innerHTML || html
  }
}

/**
 * Pack consecutive top-level elements into chunks whose total source height
 * stays within `maxChunkSourceHeight`. A single element taller than the cap
 * is allowed through on its own — it'll still exceed the canvas limit when
 * rendered alone (rare, e.g. giant tables), but splitting mid-element is
 * out of scope for this pass.
 */
function chunkHtmlByHeight(
  childInfos: ChildInfo[],
  maxChunkSourceHeight: number,
  fallbackHtml: string
): string[] {
  const chunks: string[] = []
  let currentHtml = ''
  let currentHeight = 0
  for (const info of childInfos) {
    if (currentHeight + info.height > maxChunkSourceHeight && currentHtml.length > 0) {
      chunks.push(currentHtml)
      currentHtml = ''
      currentHeight = 0
    }
    currentHtml += info.html
    currentHeight += info.height
  }
  if (currentHtml.length > 0) chunks.push(currentHtml)
  if (chunks.length === 0) chunks.push(fallbackHtml)
  return chunks
}

function isContentPixel(data: Uint8ClampedArray, index: number): boolean {
  const alpha = data[index + 3]
  if (alpha < 8) return false

  const red = data[index]
  const green = data[index + 1]
  const blue = data[index + 2]
  const brightness = (red + green + blue) / 3
  const chroma = Math.max(red, green, blue) - Math.min(red, green, blue)

  return brightness < 232 || (chroma > 24 && brightness < 248)
}

function isBlankCanvasRow(ctx: CanvasRenderingContext2D, y: number, width: number): boolean {
  const row = ctx.getImageData(0, y, width, 1).data
  const step = Math.max(1, Math.floor(width / 200))
  let samples = 0
  let contentSamples = 0

  for (let x = 0; x < width; x += step) {
    const index = x * 4
    samples += 1
    if (isContentPixel(row, index)) contentSamples += 1
  }

  return contentSamples <= Math.max(2, Math.floor(samples * 0.03))
}

function compactLargeBlankRows(canvas: HTMLCanvasElement, maxBlankRun = 96): HTMLCanvasElement {
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return canvas

  const segments: Array<{ sourceY: number; height: number }> = []
  let sourceCursor = 0
  let blankStart: number | null = null

  for (let y = 0; y < canvas.height; y++) {
    if (isBlankCanvasRow(ctx, y, canvas.width)) {
      blankStart ??= y
      continue
    }

    if (blankStart !== null) {
      const blankHeight = y - blankStart
      if (blankHeight > maxBlankRun) {
        segments.push({ sourceY: sourceCursor, height: blankStart + maxBlankRun - sourceCursor })
        sourceCursor = y
      }
      blankStart = null
    }
  }

  if (blankStart !== null && canvas.height - blankStart > maxBlankRun) {
    segments.push({ sourceY: sourceCursor, height: blankStart + maxBlankRun - sourceCursor })
    sourceCursor = canvas.height
  }

  if (sourceCursor < canvas.height) {
    segments.push({ sourceY: sourceCursor, height: canvas.height - sourceCursor })
  }

  if (segments.length === 1 && segments[0].sourceY === 0 && segments[0].height === canvas.height) {
    return canvas
  }

  const compacted = document.createElement('canvas')
  compacted.width = canvas.width
  compacted.height = segments.reduce((sum, segment) => sum + segment.height, 0)
  const compactedCtx = compacted.getContext('2d')
  if (!compactedCtx) return canvas

  let destY = 0
  for (const segment of segments) {
    compactedCtx.drawImage(
      canvas,
      0,
      segment.sourceY,
      canvas.width,
      segment.height,
      0,
      destY,
      canvas.width,
      segment.height
    )
    destY += segment.height
  }

  return compacted
}

async function generatePdfBlobFromParts(parts: PdfHtmlParts): Promise<Blob> {
  const renderWidth = parts.renderWidth ?? 760
  const renderScale = parts.renderScale ?? DEFAULT_PDF_RENDER_SCALE
  const imageQuality = parts.imageQuality ?? DEFAULT_PDF_IMAGE_QUALITY
  const maxChunkSourceHeight = Math.floor(MAX_CANVAS_HEIGHT / renderScale)
  // Step 1: render once in a measure iframe so we can read each top-level
  // child's offsetHeight and capture its outerHTML. This drives chunking.
  const measureFrame = makeHiddenIframe(renderWidth)
  try {
    const measureDoc = measureFrame.contentDocument
    if (!measureDoc) throw new Error('Could not access measure iframe document')
    measureDoc.open()
    measureDoc.write(PDF_IFRAME_HTML(parts.head, parts.body))
    measureDoc.close()
    measureFrame.style.height = measureDoc.body.scrollHeight + 'px'
    normalizeRenderedHtmlDocumentForExport(measureDoc)
    await waitForImages(measureDoc)
    normalizeRenderedHtmlDocumentForExport(measureDoc)
    measureFrame.style.height = measureDoc.body.scrollHeight + 'px'

    const childInfos: ChildInfo[] = Array.from(measureDoc.body.children).map(el => ({
      html: (el as HTMLElement).outerHTML,
      height: (el as HTMLElement).offsetHeight
    }))
    const chunks = chunkHtmlByHeight(childInfos, maxChunkSourceHeight, parts.body)

    // Step 2: render each chunk in a fresh iframe and paginate into the PDF.
    const pdf = new jsPDF('p', 'mm', 'a4')
    let cursorY = PDF_PAGE_MARGIN_MM
    for (let i = 0; i < chunks.length; i++) {
      const chunkFrame = makeHiddenIframe(renderWidth)
      try {
        const chunkDoc = chunkFrame.contentDocument
        if (!chunkDoc) throw new Error('Could not access chunk iframe document')
        chunkDoc.open()
        chunkDoc.write(PDF_IFRAME_HTML(parts.head, chunks[i]))
        chunkDoc.close()
        chunkFrame.style.height = chunkDoc.body.scrollHeight + 'px'
        normalizeRenderedHtmlDocumentForExport(chunkDoc)
        await waitForImages(chunkDoc)
        normalizeRenderedHtmlDocumentForExport(chunkDoc)
        chunkFrame.style.height = chunkDoc.body.scrollHeight + 'px'

        const canvas = await html2canvas(chunkDoc.body, {
          scale: renderScale,
          useCORS: true,
          backgroundColor: '#ffffff',
          windowWidth: renderWidth
        })

        if (canvas.width === 0 || canvas.height === 0) {
          throw new Error(`Chunk ${i + 1}/${chunks.length} produced a zero-dimension canvas`)
        }

        const canvasForPdf = parts.compactLargeBlankRows ? compactLargeBlankRows(canvas) : canvas
        cursorY = paginateCanvasIntoPdf(canvasForPdf, pdf, cursorY, imageQuality)
      } finally {
        document.body.removeChild(chunkFrame)
      }
    }

    return pdf.output('blob')
  } finally {
    document.body.removeChild(measureFrame)
  }
}

async function generatePdfBlob(htmlContent: string): Promise<Blob> {
  return generatePdfBlobFromParts({
    head: `<style>${PDF_RENDER_STYLE}</style>`,
    body: htmlContent,
    renderWidth: 760
  })
}

async function generatePdfBlobFromRenderedHtml(
  html: string,
  options: RenderedHtmlPdfOptions = {}
): Promise<Blob> {
  const parts = parseFullHtmlDocument(html)
  return generatePdfBlobFromParts({
    ...parts,
    head: `${parts.head}${RENDERED_HTML_EXPORT_STYLE_TAG}`,
    renderWidth: options.renderWidth ?? 1200,
    renderScale: RENDERED_HTML_PDF_RENDER_SCALE,
    imageQuality: RENDERED_HTML_PDF_IMAGE_QUALITY,
    compactLargeBlankRows: true
  })
}

async function generateDocxBlob(markdown: string): Promise<Blob> {
  listInstanceCounter = 0
  numberingConfigs.length = 0

  const tree = unified().use(remarkParse).use(remarkGfm).parse(markdown) as MdNode

  // Collect definition nodes into a map (identifier -> url) for resolving imageReference nodes.
  // Markdown reference-style images use: ![alt][id] with [id]: url defined elsewhere.
  const defs: DefinitionMap = new Map()
  for (const child of tree.children ?? []) {
    if (child.type === 'definition' && child.identifier && child.url) {
      defs.set(child.identifier, child.url)
    }
  }
  if (defs.size > 0) console.log('[DOCX] Resolving', defs.size, 'image reference definitions')

  const docElements = (
    await Promise.all((tree.children ?? []).map(c => convertNode(c, defs)))
  ).flat()

  return await Packer.toBlob(
    new Document({
      numbering: { config: numberingConfigs },
      sections: [
        {
          properties: { page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } } },
          children: docElements
        }
      ],
      styles: { default: { document: { run: { font: 'Calibri', size: 24 } } } }
    })
  )
}

export {
  preprocessContent,
  base64ToBinary,
  getDimensionsFromBytes,
  extractInlineRuns,
  convertNode,
  convertTable,
  getTextLength,
  markdownToHtml,
  downloadBlob,
  generatePdfBlob,
  generatePdfBlobFromRenderedHtml,
  generateDocxBlob,
  createImageParagraph,
  convertListItems,
  createNumberingConfig,
  chunkHtmlByHeight,
  compactLargeBlankRows,
  paginateCanvasIntoPdf,
  isBlankCanvasRow,
  MAX_CHUNK_SOURCE_HEIGHT
}
export type { MdNode, DefinitionMap, ChildInfo }

export function DownloadDocumentDropdown({
  content,
  filename = 'document',
  label = 'Download',
  variant = 'primary'
}: DownloadDocumentDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const processedContent = preprocessContent(content)
  const isToolbarVariant = variant === 'toolbar'

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleDownload = useCallback(
    async (format: 'md' | 'pdf' | 'docx') => {
      setIsOpen(false)

      if (format === 'md') {
        downloadBlob(new Blob([processedContent], { type: 'text/markdown' }), `${filename}.md`)
        return
      }

      setIsGenerating(true)
      try {
        const blob =
          format === 'pdf'
            ? await generatePdfBlob(markdownToHtml(processedContent))
            : await generateDocxBlob(processedContent)
        downloadBlob(blob, `${filename}.${format}`)
      } catch (err) {
        console.error(`${format.toUpperCase()} generation failed:`, err)
        BetterToast.error(
          `Failed to generate ${format.toUpperCase()}. Try downloading as Markdown instead.`
        )
      } finally {
        setIsGenerating(false)
      }
    },
    [processedContent, filename]
  )

  return (
    <div
      className={`relative flex justify-end ${isToolbarVariant ? '' : 'px-2'}`}
      ref={dropdownRef}
    >
      {isToolbarVariant ? (
        <DownloadProgressButton
          type='button'
          onClick={() => !isGenerating && setIsOpen(prev => !prev)}
          disabled={isGenerating}
          isLoading={isGenerating}
          className='h-9 px-3 rounded-xl border border-tokens-neutral-border-1 bg-white text-tokens-neutral-text-primary flex items-center justify-center gap-1 hover:bg-tokens-neutral-fill-fill-6 disabled:cursor-wait'
        >
          <IoMdDownload size={16} />
          <span>{label}</span>
          <PopoverNavDownIcon />
        </DownloadProgressButton>
      ) : (
        <DownloadProgressButton
          type='button'
          onClick={() => !isGenerating && setIsOpen(prev => !prev)}
          disabled={isGenerating}
          isLoading={isGenerating}
          className='h-9 flex p-[8px_16px] justify-center items-center gap-2 text-base rounded-[12px] border border-gray-300 bg-black text-white hover:bg-gray-800 active:bg-gray-700 disabled:cursor-wait'
        >
          <span className='flex items-center gap-2'>
            <IoMdDownload size={16} />
            <span className='font-normal'>{label}</span>
            <svg
              width='10'
              height='6'
              viewBox='0 0 10 6'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M5.00004 4.29293L1.35359 0.646484L0.646484 1.35359L5.00004 5.70714L9.35359 1.35359L8.64648 0.646484L5.00004 4.29293Z'
                fill='white'
              />
            </svg>
          </span>
        </DownloadProgressButton>
      )}

      {isOpen && (
        <SelectorDropdownContent>
          <SelectorDropdownItem hasDivider onClick={() => handleDownload('md')}>
            <span className='text-base text-tokens-neutral-text-primary'>Markdown (.md)</span>
          </SelectorDropdownItem>
          <SelectorDropdownItem hasDivider onClick={() => handleDownload('pdf')}>
            <span className='text-base text-tokens-neutral-text-primary'>PDF Document (.pdf)</span>
          </SelectorDropdownItem>
          <SelectorDropdownItem onClick={() => handleDownload('docx')}>
            <span className='text-base text-tokens-neutral-text-primary'>
              Microsoft Word (.docx)
            </span>
          </SelectorDropdownItem>
        </SelectorDropdownContent>
      )}
    </div>
  )
}
